import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SessionContext } from 'src/providers/session/SessionContext.tsx';
import { PRE_AUTH_PAGES_MAP, VISIONX_CONSTANTS } from 'src/app/ComponentMap.tsx';
import { IMenuService } from 'src/providers/system/types.ts';

const normalizePath = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);
export const isSafeReturnUrl = (url: string) => {
  if (!url) return false;
  if (!url.startsWith('/')) return false;
  if (url.startsWith('//')) return false;
  return normalizePath(url) !== normalizePath(VISIONX_CONSTANTS.LOGIN_PATH);
};
type ReturnUrlCheck = {
  returnUrl: string;
  safe: boolean;
};

const getSearchParams = (input: string): URLSearchParams => {
  // input can be "/login?x", "?x", "x", or absolute URL
  try {
    const u = new URL(input, 'https://local.invalid');
    return u.searchParams;
  } catch {
    return new URLSearchParams(input.startsWith('?') ? input : `?${input}`);
  }
};

export const extractSafeReturnUrl = (
  input: string,
  opts?: { maxDepth?: number },
): ReturnUrlCheck => {
  const maxDepth = opts?.maxDepth ?? 10;

  if (!input) return { returnUrl: '', safe: false };

  let candidate = input;

  for (let i = 0; i < maxDepth; i++) {
    const params = getSearchParams(candidate);
    const nested = params.get('returnUrl');

    if (!nested) break;

    candidate = nested;
  }

  if (!candidate || !isSafeReturnUrl(candidate)) {
    return { returnUrl: '', safe: false };
  }

  return { returnUrl: candidate, safe: true };
};

interface UseAuthGuardProps {
  validSession: boolean;
  hasSession: boolean;
  systemLoaded: boolean;
  sessionLoading: boolean;
  homePage?: string;
  menuService: IMenuService;
}

export const useAuthGuard = ({
  validSession,
  hasSession,
  systemLoaded,
  sessionLoading,
  homePage,
  menuService,
}: UseAuthGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionContext = useContext(SessionContext);
  const [navigatedToOriginalPath, setNavigatedToOriginalPath] = useState<boolean>(false);

  const preAuthPaths: string[] = useMemo(
    () =>
      Object.values(PRE_AUTH_PAGES_MAP)
        .flat()
        .map((route) => route.path)
        .filter((p): p is string => p !== undefined),
    [],
  );

  const originalPath = sessionContext.getOriginalPath() || '';

  const redirect = menuService.redirectTo(
    originalPath,
    validSession,
    hasSession,
    systemLoaded,
    homePage,
  );

  useEffect(() => {
    if (navigatedToOriginalPath) {
      sessionContext.setOriginalPath('');
    }
  }, [navigatedToOriginalPath]);

  useEffect(() => {
    // Don't redirect while session validation is still in progress (e.g. during page reload)
    if (sessionLoading) return;

    const totpSetup = JSON.parse(sessionStorage.getItem('totpSetup') || 'false');

    if (!validSession && !hasSession && !systemLoaded) {
      const currentPath = location.pathname;

      const isPreAuthRoute = preAuthPaths.some((path) => {
        const cleanPath = path.replace(/:\w+/g, '[^/]+');
        const regex = new RegExp(`^${cleanPath}$`);
        return regex.test(currentPath);
      });

      if (!isPreAuthRoute) {
        navigate(VISIONX_CONSTANTS.LOGIN_PATH);
        return;
      }
    }

    if (
      redirect &&
      redirect !== '/undefined' &&
      !redirect.includes('/login') &&
      !totpSetup &&
      !navigatedToOriginalPath
    ) {
      if (redirect === '/' && validSession && hasSession && homePage) {
        navigate(homePage, { replace: true });
      } else {
        navigate(redirect, { replace: true });
      }

      setNavigatedToOriginalPath(true);
    }
  }, [
    validSession,
    hasSession,
    systemLoaded,
    sessionLoading,
    location.pathname,
    preAuthPaths,
    homePage,
    navigate,
    menuService,
  ]);
};
